/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
/**
 * @prop {String} label - Defines the label of the card that will be displayed on top of the content.
 * @prop {String} image - Takes a url or app asset. If defined (along with the labelproperty), then the image card style will be applied. If defined (without the label property), then only the image will be displayed as a card. It is necessary to define the height of the card in this particular case. Images of all aspect-ratios can be displayed, but is sized to display them in a 16:9 aspect-ratio.
 * @prop {String} imageContain - If defined, contains the image in the image container.
 * @prop {String} imageIcon - Defines the icon when the image is unavailable, which is displayed as a placeholder.
 * @prop {String} icon - Defines the icon when the image is unavailable, which is displayed as a placeholder.
 * @prop {String} href - Takes a path to an internal page in the application. When defined, it converts the card into a linked card, and displays hover styles.
 * @prop {String} cardHeight - Defines the height of the card. Takes a pixel value.
 * @attr {String} card-height - Defines the height of the card. Takes a pixel value.
 * @prop - Slot where the main content is rendered. Takes any component as child.
 * @prop header - Takes children such as tabs.
 * @prop footer - Takes buttons as children.
 * @prop functions - Takes icons as children
 */
export class SHCard extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      image: { type: String, reflect: true },
      href: { type: String, reflect: true },
      imageIcon: { type: String, reflect: true, attribute: 'image-icon' },
      imageContain: {
        type: Boolean,
        reflect: true,
        attribute: 'image-contain',
      },
      // readonly properties
      /** @ignore */
      _emptyFooter: { type: Boolean },
      /** @ignore */
      _emptyHeader: { type: Boolean },
      /** @ignore */
      _emptyContent: { type: Boolean },

      // legacy
      cardHeight: { type: String, reflect: true, attribute: 'card-height' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
        }
        .card-wrapper {
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
          border-radius: 0.125rem;
          background-color: var(--base-3);
          box-shadow: var(--shadow-raised);
          transition: var(--transition-time) all ease-in-out;
        }
        .label-wrapper {
          display: flex;
          flex-direction: row;
          padding: 1rem;
          align-items: center;
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
        }
        sh-icon.card-icon {
          margin-right: 0.5rem;
        }
        .card-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: bold;
          line-height: 1.5rem !important;
          flex: 1;
          color: var(--text-primary);
          font: var(--header-2);
          -webkit-font-smoothing: antialiased !important;
          text-rendering: optimizeLegibility !important;
          -moz-osx-font-smoothing: grayscale !important;
          max-width: 100%;
        }
        /* slots */
        .label-wrapper + .header-wrapper {
          padding-top: 0rem;
        }
        slot {
          display: flex;
        }
        .header-wrapper,
        slot[name='footer'] {
          padding: 1rem;
        }
        slot:not([name]) {
          padding: 0rem 1rem;
        }
        slot[name='functions'] {
          align-items: center;
        }
        .header-wrapper {
          height: fit-content;
          overflow: hidden;
        }
        slot:not([name]) {
          flex: 1;
          box-sizing: border-box;
          flex-direction: column;
          overflow: auto;
          font: var(--body-1);
          color: var(--text-primary);
          line-height: 1.25rem;
        }
        slot[name='footer'] {
          height: fit-content;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
        }
        slot[name='header']::slotted(sh-tabs) {
          margin-top: -1rem;
        }
        slot:not([name]).empty-header:not(.empty-content) {
          padding-top: 1rem;
        }
        slot:not([name]).empty-footer:not(.empty-content) {
          padding-bottom: 1rem;
        }
        slot.empty,
        .header-wrapper.empty {
          padding: 0rem;
        }
        /* image */
        .image-wrapper {
          height: 0rem;
          padding-top: 56.25%;
          position: relative;
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .image-wrapper sh-icon,
        .image-wrapper img {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate3d(-50%, -50%, 0);
        }
        .image-wrapper sh-icon {
          --sh-icon-size-m: 4rem;
          --sh-icon-font-size-m: 4rem;
        }
        img {
          height: 100%;
          width: 100%;
          display: block;
          background-position: center;
          object-fit: cover;
          background-repeat: no-repeat;
        }
        :host([image-contain]) img {
          object-fit: contain;
        }
        :host([image][href]) .label-wrapper,
        :host([image]) .label-wrapper,
        :host([image-icon]) .label-wrapper {
          padding-bottom: 0.5rem;
        }
        /* Content Spacing */
        slot:not([name])::slotted(*:not(:first-child)) {
          margin-top: 0.5rem;
        }
        slot[name='functions']::slotted(*),
        slot[name='footer']::slotted(*:not(:first-child)) {
          margin-left: 0.5rem;
        }
        /* href */
        :host([href]) {
          cursor: pointer;
        }
        :host([href]:hover) .card-wrapper {
          box-shadow: var(--shadow-overlay);
        }
        .card-wrapper::before {
          pointer-events: none;
          transition: var(--transition-time) all ease-in-out;
          content: '';
          border-radius: 0.125rem;
          height: 100%;
          width: 100%;
          position: absolute;
        }
        :host([href]:hover) .card-wrapper::before {
          background-color: rgba(var(--ui-5), var(--opacity-7));
        }
        :host([href]:not([image])) .label-wrapper.empty-content {
          padding: 1rem;
          height: 100%;
          align-items: center;
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: center;
        }
        :host([href]:not([image])) .label-wrapper.empty-content:not(.empty-footer) {
          padding-bottom: 0rem;
        }
        :host([href]) .label-wrapper.empty-content .card-label {
          flex: unset;
        }
        :host([href]:not([image])) .card-icon.empty-content {
          margin-right: unset;
          margin-bottom: 0.5rem;
        }
        :host([href]) slot[name='footer']::slotted(*:not(:first-child)) {
          margin: unset;
        }
        :host([href]:not([image])) slot[name='footer']:not(.empty).empty-content {
          border-top: 0.0625rem solid rgba(var(--ui-1), var(--opacity-6));
        }
        :host([href]:not([image])) slot[name='footer']:not(.empty) {
          margin: 0 1rem;
          padding: 1rem 0;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        /* touch */
        @media (pointer: coarse) {
          slot:not([name])::slotted(*:not(:first-child)) {
            margin-top: 1rem;
          }
          slot[name='functions']::slotted(*:not(:first-child)),
          slot[name='footer']::slotted(*:not(:first-child)) {
            margin-left: 1rem;
          }
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="card-wrapper">
        ${this.image || this.imageIcon
          ? html`
              <div class="image-wrapper">
                ${this.image ? html` <img src="${this.image}" /> ` : ''}
                ${this.imageIcon && !this.image
                  ? html`
                      <sh-icon
                        color="rgba(var(--ui-1), var(--opacity-5))"
                        icon="${this.imageIcon}"
                        class="image-icon"
                      ></sh-icon>
                    `
                  : ''}
              </div>
            `
          : ''}
        ${this.label
          ? html`
              <div
                class="label-wrapper ${this._emptyFooter ? 'empty-footer' : ''} ${this._emptyContent
                  ? 'empty-content'
                  : ''}"
              >
                ${this.icon
                  ? html`
                      <sh-icon
                        size="${(this.href || this.hasAttribute('href')) &&
                        !this.image &&
                        this._emptyContent
                          ? 'l'
                          : 's'}"
                        icon="${this.icon}"
                        class="card-icon ${this._emptyContent ? 'empty-content' : ''}"
                      ></sh-icon>
                    `
                  : ''}
                <div class="card-label">${this.label}</div>
                ${!(this._emptyContent && this.href) ? html` <slot name="functions"></slot> ` : ''}
              </div>
            `
          : ''}
        ${!this.href
          ? html`
              <div class="header-wrapper ${this._emptyHeader ? 'empty' : ''}">
                <slot
                  name="header"
                  @slotchange="${(e) => this.updateSlotFlag(e.target, '_emptyHeader')}"
                ></slot>
              </div>
            `
          : ''}
        <slot
          class="${this._emptyHeader && !this.label ? 'empty-header' : ''} ${this._emptyFooter
            ? 'empty-footer'
            : ''} ${this._emptyContent ? 'empty-content' : ''}"
          @slotchange="${(e) => this.updateContentSlotFlag(e.target, '_emptyContent')}"
        ></slot>
        <slot
          name="footer"
          @slotchange="${(e) => this.updateSlotFlag(e.target, '_emptyFooter')}"
          class="${this._emptyFooter ? 'empty' : ''} ${this._emptyContent ? 'empty-content' : ''}"
        ></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this._emptyFooter = true;
    this._emptyHeader = true;
    this._emptyContent = true;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
  }

  updated(changedProperties) {
    const listOfProperties = ['label', 'icon', 'image', 'cardHeight'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
    if (changedProperties.has('cardHeight') && this) {
      console.warn(
        'You are using a legacy property (cardHeight) of the "sh-card" component. For adjusting the height of the Card please use CSS styles instead.'
      );
      this.handleCardHeight();
    }
  }

  firstUpdated() {
    this.addEventListener('click', () => {
      if (this.href) {
        window.location.href = this.href;
      }
    });
  }

  updateContentSlotFlag(slot, emptySlotFlag) {
    let assignedNodes = Array.from(slot.assignedNodes()).filter((el) =>
      el.data && el.tagName !== 'SH-CHART'
        ? el.data.trim().split('\n').join('').split(' ').join('')
        : ''
    );
    this[emptySlotFlag] = assignedNodes.length === 0 && slot.assignedElements().length === 0;
  }

  // check if the slot is empty and update the property accordingly
  updateSlotFlag(slot, emptySlotFlag) {
    this[emptySlotFlag] =
      slot.assignedNodes({
        flatten: true,
      }).length === 0;
  }

  // adjust height style
  handleCardHeight() {
    const cardWrapper = this.shadowRoot.querySelector('.card-wrapper');
    cardWrapper.style.minHeight = this.cardHeight;
    cardWrapper.style.maxHeight = this.cardHeight;
  }
}

customElements.define('sh-card', SHCard);
